import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";
import { useNotification } from "@/context/NotificationContext";
import {
  GetProcessWheelsRequest,
  ProcessWheel,
  ProcessWheelStatusFilter,
} from "../api/getProcessWheels/types";
import { ProcessWheelFormValues } from "../components/ProcessWheelForm/ProcessWheelForm";
import {
  createProcessWheelAction,
  deleteProcessWheelAction,
  getProcessWheelsAction,
  updateProcessWheelAction,
} from "../slice/processWheelSlice";

export const useProcessWheels = () => {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();

  const processWheels = useAppSelector(
    (state: RootState) => state.processWheel.processWheels,
  );
  const totalCount = useAppSelector(
    (state: RootState) => state.processWheel.totalCount,
  );
  const loading = useAppSelector(
    (state: RootState) => state.processWheel.loadingList,
  );
  const error = useAppSelector(
    (state: RootState) => state.processWheel.listError,
  );
  const loadingCreate = useAppSelector(
    (state: RootState) => state.processWheel.loadingCreate,
  );
  const loadingUpdate = useAppSelector(
    (state: RootState) => state.processWheel.loadingUpdate,
  );
  const loadingDelete = useAppSelector(
    (state: RootState) => state.processWheel.loadingDelete,
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState<ProcessWheelStatusFilter>("all");

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingProcessWheel, setEditingProcessWheel] =
    useState<ProcessWheel | null>(null);
  const [viewingProcessWheel, setViewingProcessWheel] =
    useState<ProcessWheel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProcessWheel | null>(null);

  const requestData = useMemo<GetProcessWheelsRequest>(
    () => ({
      searchTerm: searchTerm || undefined,
      status,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy: sorting.length
        ? (sorting[0].id as "name" | "description")
        : undefined,
      sortOrder: sorting.length
        ? sorting[0].desc
          ? "DESC"
          : "ASC"
        : undefined,
    }),
    [searchTerm, status, pagination, sorting],
  );

  const refetch = useCallback(() => {
    dispatch(getProcessWheelsAction(requestData));
  }, [dispatch, requestData]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleStatusChange = useCallback(
    (nextStatus: ProcessWheelStatusFilter) => {
      setStatus(nextStatus);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [],
  );

  const openCreate = useCallback(() => setIsCreateOpen(true), []);
  const closeCreate = useCallback(() => setIsCreateOpen(false), []);

  const openEdit = useCallback(
    (processWheel: ProcessWheel) => setEditingProcessWheel(processWheel),
    [],
  );
  const closeEdit = useCallback(() => setEditingProcessWheel(null), []);

  const openView = useCallback(
    (processWheel: ProcessWheel) => setViewingProcessWheel(processWheel),
    [],
  );
  const closeView = useCallback(() => setViewingProcessWheel(null), []);

  const editFromView = useCallback((processWheel: ProcessWheel) => {
    setViewingProcessWheel(null);
    setEditingProcessWheel(processWheel);
  }, []);

  const requestDelete = useCallback(
    (processWheel: ProcessWheel) => setDeleteTarget(processWheel),
    [],
  );
  const closeDelete = useCallback(() => setDeleteTarget(null), []);

  const toPayload = (values: ProcessWheelFormValues) => ({
    name: values.name,
    description: values.description.trim() ? values.description : null,
  });

  const submitCreate = useCallback(
    (values: ProcessWheelFormValues) => {
      dispatch(
        createProcessWheelAction(toPayload(values), () => {
          showNotification(
            "Success",
            "Process wheel created successfully.",
            "success",
          );
          setIsCreateOpen(false);
          refetch();
        }),
      );
    },
    [dispatch, refetch, showNotification],
  );

  const submitEdit = useCallback(
    (values: ProcessWheelFormValues) => {
      if (!editingProcessWheel) {
        return;
      }
      dispatch(
        updateProcessWheelAction(
          editingProcessWheel.uuid,
          toPayload(values),
          () => {
            showNotification(
              "Success",
              "Process wheel updated successfully.",
              "success",
            );
            setEditingProcessWheel(null);
            refetch();
          },
        ),
      );
    },
    [dispatch, editingProcessWheel, refetch, showNotification],
  );

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) {
      return;
    }
    dispatch(
      deleteProcessWheelAction(deleteTarget.uuid, () => {
        showNotification(
          "Process Wheel Deleted",
          "The process wheel was deleted successfully.",
          "success",
        );
        setDeleteTarget(null);
        refetch();
      }),
    );
  }, [dispatch, deleteTarget, refetch, showNotification]);

  return {
    processWheels,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    searchTerm,
    status,
    handlePaginationChange,
    handleSortingChange,
    handleSearchChange,
    handleStatusChange,

    loadingCreate,
    loadingUpdate,
    loadingDelete,

    isCreateOpen,
    openCreate,
    closeCreate,

    editingProcessWheel,
    openEdit,
    closeEdit,

    viewingProcessWheel,
    openView,
    closeView,
    editFromView,

    deleteTarget,
    requestDelete,
    closeDelete,

    submitCreate,
    submitEdit,
    confirmDelete,
  };
};
